// src/hooks/useFeatureFlagOverrides.ts
//
// AUTHORITY (FF-D):
//   Overrides are UI / testing exposure only. They do NOT grant
//   server authority. Server-side entitlement enforcement (FF-A
//   labor trigger on `ticket_jobs`, FF-B parts/pricing triggers on
//   `parts_lines` + `shop_pricing_*`) is the binding authority for
//   all paid-feature mutations. Flipping a flag client-side via
//   sessionStorage cannot escalate into the server domain.
//
//   Per-flag overrides are kept as a developer / internal-admin
//   debugging tool. The previously-exported "nuclear" helpers
//   (`setAllEntitlementFlags(true)` / `setAllExposureFlags(true)`)
//   that flipped every flag in one call have been REMOVED from the
//   module surface. Their equivalent is now inlined inside the
//   admin-gated `FeatureFlagsAdmin` page so no non-admin code path
//   can import them. See FF-D closure memo for rationale.
//
// sessionStorage-backed feature flag overrides for internal admin
// debugging. Overrides survive page refresh but not tab close.

import { useFeatureStore } from '@/stores/featureStore';
import { getFeatureFlags, getExposureFlags, type PlanTier, type FeatureFlags, type ExposureFlags } from '@/lib/features/featureFlags';

const FF_KEY = 'ff_overrides';
const EF_KEY = 'ef_overrides';

/** Read stored override maps from sessionStorage */
export function getStoredOverrides(): {
  featureOverrides: Partial<FeatureFlags>;
  exposureOverrides: Partial<ExposureFlags>;
} {
  try {
    const ff = sessionStorage.getItem(FF_KEY);
    const ef = sessionStorage.getItem(EF_KEY);
    return {
      featureOverrides: ff ? JSON.parse(ff) : {},
      exposureOverrides: ef ? JSON.parse(ef) : {},
    };
  } catch {
    return { featureOverrides: {}, exposureOverrides: {} };
  }
}

/** Check if any overrides are currently stored */
export function hasStoredOverrides(): boolean {
  const { featureOverrides, exposureOverrides } = getStoredOverrides();
  return Object.keys(featureOverrides).length > 0 || Object.keys(exposureOverrides).length > 0;
}

/** Persist overrides and update Zustand store */
export function applyOverrides(
  featureOverrides: Partial<FeatureFlags>,
  exposureOverrides: Partial<ExposureFlags>,
) {
  const { featureFlags, exposureFlags, setFeatureFlags, setExposureFlags } = useFeatureStore.getState();

  if (featureFlags && Object.keys(featureOverrides).length > 0) {
    sessionStorage.setItem(FF_KEY, JSON.stringify(featureOverrides));
    setFeatureFlags({ ...featureFlags, ...featureOverrides });
  }

  if (exposureFlags && Object.keys(exposureOverrides).length > 0) {
    sessionStorage.setItem(EF_KEY, JSON.stringify(exposureOverrides));
    setExposureFlags({ ...exposureFlags, ...exposureOverrides });
  }
}

/** Apply stored overrides on top of current Zustand state (call after flag init) */
export function applyStoredOverrides() {
  const { featureOverrides, exposureOverrides } = getStoredOverrides();
  if (Object.keys(featureOverrides).length === 0 && Object.keys(exposureOverrides).length === 0) return;
  applyOverrides(featureOverrides, exposureOverrides);
}

/** Clear all overrides and recompute from tier defaults */
export function clearAllOverrides(plan: PlanTier, addons: string[]) {
  sessionStorage.removeItem(FF_KEY);
  sessionStorage.removeItem(EF_KEY);
  const { setFeatureFlags, setExposureFlags } = useFeatureStore.getState();
  setFeatureFlags(getFeatureFlags(plan, addons));
  setExposureFlags(getExposureFlags(plan, addons));
}

// FF-D: `setAllEntitlementFlags(value)` and `setAllExposureFlags(value)`
// have been REMOVED from this module. Reason: they were unrestricted
// module-level exports that any code could import to flip every flag
// at once. The audit classified that as RISK-5 (override escalation).
//
// The "Enable All / Disable All" admin buttons in
// `src/pages/FeatureFlagsAdmin.tsx` now inline an equivalent loop
// directly inside the admin-gated component. The admin page itself is
// triple-gated (route-level `<InternalAdminRoute>` + page-level
// `useIsInternalAdmin()` check + per-render `isAdmin` guard inside
// the inline functions), so the only way to fire "Enable All" is
// from a verified-admin session.
//
// Per-flag overrides remain available via the existing
// `applyOverrides()` helper above. That helper is bounded — it merges
// caller-supplied partial maps; it cannot fabricate a fully-true map
// without the caller already enumerating every key. Callers that
// enumerate every key are by definition the admin page.
